import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { PodcastEpisodeModel } from '../../../models/Podcast.model';

/** Saved listening progress for a single podcast episode (APPS-2159). */
export type EpisodeProgress = {
  podcastId: string;
  episodeId: string;
  /** Last known playback position, in seconds. */
  position: number;
  /** Episode duration, in seconds (0 when still unknown). */
  duration: number;
  /** Epoch ms of the last save, so the list can be sorted / pruned later. */
  updatedAt: number;
  episode?: PodcastEpisodeModel;
};

export type PodcastListSliceState = {
  readList: string[][];
  /** Per-episode listening progress, keyed by `podcastId::episodeId`. */
  progressByEpisode: Record<string, EpisodeProgress>;
};

/** Composite key used to store one progress entry per episode. */
export const episodeProgressKey = (podcastId: string, episodeId: string): string =>
  `${podcastId}::${episodeId}`;

/** Ignore saved positions this close to the start (in seconds). */
export const RESUME_MIN_POSITION = 5;
export const EPISODE_FINISHED_RATIO = 0.95;
/** Ratio past which an episode is considered finished and restarts from 0. */
export const RESUME_END_RATIO = EPISODE_FINISHED_RATIO;

export const getResumePosition = (
  progress: EpisodeProgress | undefined,
  isRead: boolean,
): number => {
  if (!progress || isRead) {
    return 0;
  }
  const { position, duration } = progress;
  if (position < RESUME_MIN_POSITION) {
    return 0;
  }
  if (duration > 0 && position >= RESUME_END_RATIO * duration) {
    return 0;
  }
  return position;
};

const initialState: PodcastListSliceState = {
  readList: [],
  progressByEpisode: {},
};

const podcastListSlice = createSlice({
  name: 'podcastList',
  initialState,
  reducers: {
    updatePodcastReadList(state, action) {
      state.readList = action.payload;
    },
    /** Clears the "read" flag of a single episode, e.g. when it is relaunched. */
    removeEpisodeFromReadList(
      state,
      action: PayloadAction<{ podcastId: string; episodeId: string }>,
    ) {
      const { podcastId, episodeId } = action.payload;
      state.readList = state.readList.filter(
        ([pId, eId]) => !(pId === podcastId && eId === episodeId),
      );
    },
    saveEpisodeProgress(state, action: PayloadAction<EpisodeProgress>) {
      const { podcastId, episodeId } = action.payload;
      state.progressByEpisode[episodeProgressKey(podcastId, episodeId)] = action.payload;
    },
  },
});
export const { updatePodcastReadList, removeEpisodeFromReadList, saveEpisodeProgress } =
  podcastListSlice.actions;
export const podcastListReducer = podcastListSlice.reducer;
